import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import EyeIcon from "@styles/all-images/icons/eye-icon.svg";
import EyeOffIcon from "@styles/all-images/icons/eye-off-icon.svg";
import {
  setTempEmail,
  setOtpSent,
  setTempToken,
  setAuth,
  clearTempData,
} from "@/store/slices/authSlice";
import TermsAndConditions from "./TermsAndConditions";
import PhoneVerification from "../PhoneVerification";
interface InvitationData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  token: string;
  user_type: number;
}

const SignUpPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppThunkDispatch>();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [invitationData, setInvitationData] = useState<InvitationData | null>(
    null,
  );
  const [isTermsAndConditionsModalOpen, setIsTermsAndConditionsModalOpen] =
    useState(false);
  const [showOTPScreen, setShowOTPScreen] = useState(false);

  const handleOpenTermsAndConditions = () => {
    setIsTermsAndConditionsModalOpen(true);
  };

  const handleCloseTermsAndConditions = () => {
    setIsTermsAndConditionsModalOpen(false);
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  const validationSchema = Yup.object({
    firstName: Yup.string()
      .required("First name is required")
      .min(2, "First name must be at least 2 characters")
      .max(50, "First name must be at most 50 characters"),
    lastName: Yup.string()
      .required("Last name is required")
      .min(2, "Last name must be at least 2 characters")
      .max(50, "Last name must be at most 50 characters"),
    email: Yup.string()
      .required("Email is required")
      .email("Invalid email format"),
    phone: Yup.string()
      .required("Contact number is required")
      .matches(/^\d{10}$/, "Invalid phone number format"),
    password: Yup.string()
      .required("Password is required")
      .min(8, "Password must be at least 8 characters")
      .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
      .matches(/[a-z]/, "Password must contain at least one lowercase letter")
      .matches(/[0-9]/, "Password must contain at least one number")
      .matches(
        /[!@#$%^&*(),.?":{}|<>]/,
        "Password must contain at least one special character",
      ),
    confirmPassword: Yup.string()
      .required("Please confirm your password")
      .oneOf([Yup.ref("password")], "Passwords must match"),
  });

  const handleSubmit = async (values: any) => {
    try {
      const response: any = await sendOTP();

      dispatch(setTempEmail(values.email));
      dispatch(setTempToken(response.token || ""));
      dispatch(setOtpSent(true));
      toast.success("Please verify your phone number.");
      setShowOTPScreen(true);
    } catch (error) {
      console.error("Error during sign up:", error);
    }
  };

  const sendOTP = async () => {
    return new Promise(async (resolve, reject) => {
      try {
        setIsLoading(true);
        const { unAuthenticatedCall } = actionsHandler("sendOTP");
        const response = await dispatch(
          unAuthenticatedCall({
            payload: {
              phone: formik.values.phone,
              email: formik.values.email,
            },
            method: "post",
          }),
        );
        if (response.ok) {
          resolve(response.data);
        } else {
          reject(response.data);
        }
      } catch (error) {
        console.error("Error during sign up:", error);
        toast.error("An unexpected error occurred");
        reject(error);
      } finally {
        setIsLoading(false);
      }
    });
  };
  const submitSignUp = async () => {
    const values = formik.values;
    try {
      setIsSubmitLoading(true);
      const { unAuthenticatedCall } = actionsHandler("signUp");

      const response = await dispatch(
        unAuthenticatedCall({
          payload: {
            token: invitationData?.token,
            first_name: values.firstName,
            last_name: values.lastName,
            phone: values.phone,
            password: values.password,
            is_staff: true,
          },
          method: "post",
        }),
      );

      if (response.ok) {
        dispatch(
          setAuth({
            token: response.data.token,
            user: { email: response.data },
          }),
        );
        dispatch(clearTempData());
        toast.success("Account created successfully.");
      } else {
        toast.error(response.data.message || "Sign up failed");
      }
    } catch (error) {
      console.error("Error during sign up:", error);
      toast.error("An unexpected error occurred");
    } finally {
      setIsSubmitLoading(false);
    }
  };

  const handleOTPSuccess = () => {
    submitSignUp();
  };

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    const fetchInvitationData = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const token = params.get("token");

        if (token) {
          const { unAuthenticatedCall } = actionsHandler("getInvitation");
          const response = await dispatch(
            unAuthenticatedCall({
              payload: { token },
              method: "post",
            }),
          );

          if (response.ok) {
            setInvitationData(response.data);
            formik.setValues({
              ...formik.values,
              firstName: response.data.first_name,
              lastName: response.data.last_name,
              email: response.data.email,
              phone: response.data.phone,
            });
          } else {
            toast.error("Failed to load invitation data");
            navigate("/");
          }
        }
      } catch (error) {
        console.error("Error fetching invitation data:", error);
        toast.error("Failed to load invitation data");
        navigate("/");
      }
    };

    fetchInvitationData();
  }, [location.search]);

  return (
    <>
      {!isSubmitLoading ? (
        <>
          {!showOTPScreen ? (
            <>
              <div className="tw-min-h-screen tw-flex sm:tw-py-0 tw-py-6">
                <div className="lg:tw-w-2/6 md:tw-w-2/6 sm:tw-w-full tw-hidden login-bg tw-bg-cover tw-bg-center md:tw-flex tw-items-end tw-justify-start tw-text-white lg:tw-p-14 tw-p-6">
                  <div className="tw-px-4">
                    <h1 className="tw-font-bold font-size-5xl tw-mb-4 tw-leading-[74px]">
                      Innovative Collateral & <br /> Risk Solutions
                    </h1>
                  </div>
                </div>

                <div className="lg:tw-w-4/6 md:tw-w-4/6 sm:tw-w-full tw-w-full tw-flex tw-items-center tw-justify-center">
                  <div className="tw-w-full tw-px-12">
                    <div className="tw-mb-8">
                      <img
                        src="/logo/Logo.svg"
                        alt="Logo"
                        className="tw-mx-auto tw-max-w-[150px] tw-mb-14"
                      />
                      <h5 className="tw-text-[22px] font-primary font-primary-color font-weight-semibold tw-leading-6">
                        Sign Up
                      </h5>
                      <p className="tw-mt-2 tw-leading-6 font-size-sm font-gray-color font-weight-medium">
                        Enter your details to create your account.
                      </p>
                    </div>

                    <form onSubmit={formik.handleSubmit}>
                      <div className="tw-bg-white tw-rounded-lg tw-p-6 tw-border tw-border-gray-200 tw-mb-6">
                        <h3 className="font-gray-color-dark font-weight-semibold font-size-sm tw-mb-6">
                          Primary Contact Sign Up
                        </h3>

                        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4 tw-mb-6">
                          <div className="form-group">
                            <p className="font-gray-color-dark font-weight-medium font-size-sm tw-mb-2">
                              First Name
                            </p>
                            <input
                              type="text"
                              {...formik.getFieldProps("firstName")}
                              placeholder="John"
                              className={`tw-w-full tw-px-3 tw-py-2 tw-border ${
                                formik.touched.firstName &&
                                formik.errors.firstName
                                  ? "tw-border-red-500"
                                  : "tw-border-gray-300"
                              } tw-rounded-lg focus:tw-ring-blue-500 focus:tw-border-blue-500`}
                            />
                            {formik.touched.firstName &&
                              formik.errors.firstName && (
                                <div className="tw-text-red-500 tw-text-sm tw-mt-1">
                                  {formik.errors.firstName}
                                </div>
                              )}
                          </div>

                          <div className="form-group">
                            <p className="font-gray-color-dark font-weight-medium font-size-sm tw-mb-2">
                              Last Name
                            </p>
                            <input
                              type="text"
                              {...formik.getFieldProps("lastName")}
                              placeholder="Doe"
                              className={`tw-w-full tw-px-3 tw-py-2 tw-border ${
                                formik.touched.lastName &&
                                formik.errors.lastName
                                  ? "tw-border-red-500"
                                  : "tw-border-gray-300"
                              } tw-rounded-lg focus:tw-ring-blue-500 focus:tw-border-blue-500`}
                            />
                            {formik.touched.lastName &&
                              formik.errors.lastName && (
                                <div className="tw-text-red-500 tw-text-sm tw-mt-1">
                                  {formik.errors.lastName}
                                </div>
                              )}
                          </div>
                        </div>

                        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4 tw-mb-6">
                          <div className="form-group">
                            <p className="font-gray-color-dark font-weight-medium font-size-sm tw-mb-2">
                              Email
                            </p>
                            <input
                              type="email"
                              disabled={true}
                              {...formik.getFieldProps("email")}
                              placeholder="johndoe@gmail.com"
                              className={`tw-w-full tw-px-3 tw-py-2 tw-border ${
                                formik.touched.email && formik.errors.email
                                  ? "tw-border-red-500"
                                  : "tw-border-gray-300"
                              } tw-rounded-lg focus:tw-ring-blue-500 focus:tw-border-blue-500`}
                            />
                            {formik.touched.email && formik.errors.email && (
                              <div className="tw-text-red-500 tw-text-sm tw-mt-1">
                                {formik.errors.email}
                              </div>
                            )}
                          </div>

                          <div className="form-group">
                            <p className="font-gray-color-dark font-weight-medium font-size-sm tw-mb-2">
                              Contact Number
                            </p>
                            <input
                              type="tel"
                              {...formik.getFieldProps("phone")}
                              placeholder="+27 21 123 4567"
                              className={`tw-w-full tw-px-3 tw-py-2 tw-border ${
                                formik.touched.phone && formik.errors.phone
                                  ? "tw-border-red-500"
                                  : "tw-border-gray-300"
                              } tw-rounded-lg focus:tw-ring-blue-500 focus:tw-border-blue-500`}
                            />
                            {formik.touched.phone && formik.errors.phone && (
                              <div className="tw-text-red-500 tw-text-sm tw-mt-1">
                                {formik.errors.phone}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4">
                          <div className="form-group">
                            <p className="font-gray-color-dark font-weight-medium font-size-sm tw-mb-2">
                              Password
                            </p>
                            <div className="tw-relative">
                              <TextField
                                fullWidth
                                type={showPassword ? "text" : "password"}
                                variant="outlined"
                                placeholder="••••••••••"
                                className="tw-mb-4"
                                {...formik.getFieldProps("password")}
                                error={
                                  formik.touched.password &&
                                  Boolean(formik.errors.password)
                                }
                                helperText={
                                  formik.touched.password &&
                                  formik.errors.password
                                }
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={handleTogglePassword}
                                        edge="end"
                                        className="tw-p-1 tw-border tw-border-gray-300 tw-rounded-full hover:tw-bg-gray-100"
                                      >
                                        {showPassword ? (
                                          <img src={EyeIcon} alt="EyeIcon" />
                                        ) : (
                                          <img
                                            src={EyeOffIcon}
                                            alt="EyeOffIcon"
                                          />
                                        )}
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </div>
                          </div>

                          <div className="form-group">
                            <p className="font-gray-color-dark font-weight-medium font-size-sm tw-mb-2">
                              Confirm Password
                            </p>
                            <div className="tw-relative">
                              <TextField
                                fullWidth
                                type={showPassword ? "text" : "password"}
                                variant="outlined"
                                placeholder="••••••••••"
                                className="tw-mb-4"
                                {...formik.getFieldProps("confirmPassword")}
                                error={
                                  formik.touched.confirmPassword &&
                                  Boolean(formik.errors.confirmPassword)
                                }
                                helperText={
                                  formik.touched.confirmPassword &&
                                  formik.errors.confirmPassword
                                }
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={handleTogglePassword}
                                        edge="end"
                                        className="tw-p-1 tw-border tw-border-gray-300 tw-rounded-full hover:tw-bg-gray-100"
                                      >
                                        {showPassword ? (
                                          <img src={EyeIcon} alt="EyeIcon" />
                                        ) : (
                                          <img
                                            src={EyeOffIcon}
                                            alt="EyeOffIcon"
                                          />
                                        )}
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="tw-flex tw-justify-center tw-items-center tw-my-4">
                        <span className="tw-text-sm tw-font-semibold tw-text-[#1B1B1B]">
                          View
                          <button
                            onClick={handleOpenTermsAndConditions}
                            className="tw-text-sm tw-font-semibold tw-text-[#146C94] tw-underline tw-ml-2"
                          >
                            Terms & Conditions
                          </button>
                        </span>
                      </div>
                      <div className="tw-flex tw-justify-center tw-items-center">
                        <button
                          type="submit"
                          disabled={isLoading || !formik.isValid}
                          className="tw-w-full sm:tw-max-w-[320px] tw-py-2 primary-custom-button disabled:tw-opacity-50"
                        >
                          {isLoading ? "Signing Up..." : "Sign Up"}
                        </button>
                      </div>
                    </form>
                  </div>
                  {isTermsAndConditionsModalOpen &&
                    invitationData?.user_type &&
                    invitationData?.user_type !== 3 && (
                      <TermsAndConditions
                        user_type={invitationData?.user_type}
                        onClose={handleCloseTermsAndConditions}
                      />
                    )}
                </div>
              </div>
            </>
          ) : (
            <PhoneVerification successCallback={handleOTPSuccess} />
          )}
        </>
      ) : (
        <div className="tw-flex tw-justify-center tw-items-center tw-h-screen">
          <CircularProgress />
        </div>
      )}
    </>
  );
};

export default SignUpPage;
